<template>
    <c-modal
        :class="modalClass"
        :closeOnBackdrop="closeable"
        :scrollable="scrollable"
        :size="maxWidth"
        :teleport="teleport"
        :visible="show"
        backdrop="static"
        @close="$emit('close', $event)"
        @show="onShow"
    >
        <c-modal-header :class="headerClasses" :close-button="closeButton">
            <div class="flex-column align-items-start min-w-0 flex-grow-1">
                <c-modal-title :class="{ 'mb-2': $slots.subtitle }" as="h3">
                    <slot name="title" />
                </c-modal-title>
                <div v-if="$slots.subtitle" class="h4 modal-subtitle">
                    <slot name="subtitle" />
                </div>
            </div>
        </c-modal-header>
        <c-modal-body>
            <slot />
        </c-modal-body>
        <c-modal-footer v-if="showFooter" :class="{ 'border-top': isMobile }">
            <div class="d-flex flex-sm-row flex-column-reverse justify-content-center align-items-center m-0 w-100 gap-3">
                <slot name="footer">
                    <jet-button
                        :disabled="isProcessing || cancelDisabled"
                        type="button"
                        variant="secondary"
                        style="flex: 1"
                        @click.stop="$emit('close', $event)"
                    >
                        {{ secondaryButtonLabel || trans('general.action.cancel') }}
                    </jet-button>
                    <jet-button
                        :destructive="destructive"
                        :disabled="isProcessing || submitDisabled"
                        :is-processing="isProcessing"
                        :variant="variant"
                        style="flex: 1"
                        @click.stop="$emit('submit', $event)"
                    >
                        {{ primaryButtonText }}
                    </jet-button>
                </slot>
            </div>
        </c-modal-footer>
    </c-modal>
</template>

<script>
import { CModal, CModalTitle } from '@coreui/vue-pro/dist/esm/components/modal/index.js';
import JetButton from '@/Jetstream/Button.vue';
import Spinner from '@/Components/Spinner.vue';
import { isMobile } from '@/Utils/UseMedia';

export default {
    name: 'DialogModal',

    emits: ['close', 'submit'],

    setup() {
        return { isMobile };
    },

    components: {
        Spinner,
        JetButton,
        CModalTitle,
        CModal,
    },

    props: {
        show: {
            default: false,
        },
        maxWidth: {
            default: undefined,
        },
        closeable: {
            default: false,
        },
        closeButton: {
            default: false,
        },
        scrollable: {
            type: Boolean,
            default: true,
        },
        type: {
            type: String,
            default: 'light',
        },
        primaryButtonLabel: String,
        secondaryButtonLabel: String,
        isProcessing: {
            type: Boolean,
            default: false,
        },
        cancelDisabled: {
            type: Boolean,
            default: false,
        },
        submitDisabled: {
            type: Boolean,
            default: false,
        },
        showFooter: {
            type: Boolean,
            default: true,
        },
        teleport: {
            type: Boolean,
            default: false,
        },
        modalClass: {
            type: [String, Array, Object],
            default: null,
        },
    },

    computed: {
        headerClasses() {
            return {
                'bg-danger text-white': this.type === 'danger',
            };
        },
        primaryButtonText() {
            return this.primaryButtonLabel || this.trans('general.action.save');
        },
        destructive() {
            return this.type === 'danger';
        },
        variant() {
            if (this.type === 'danger') {
                return 'secondary';
            }
            return 'primary';
        },
    },

    methods: {
        onShow() {
            // Remove all currently displayed popovers that might potentially
            // overlay the open modal
            let elements = document.getElementsByClassName('popover');
            while (elements.length > 0) {
                elements[0].parentNode.removeChild(elements[0]);
            }
        },
    },

    beforeUnmount() {
        document.body.classList.remove('modal-open');
        document.body.style.padding = null;
        document.body.style.overflow = null;
    },
};
</script>
